import { useState } from "react";
import { LifeBuoy, CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "@/api";
import { useLanguage } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FilePicker } from "@/components/FilePicker";

const CATEGORIES = ["app_error", "trip_issue", "parcel_issue", "user_complaint", "other"];

export default function HelpSupport() {
  const { t } = useLanguage();
  const [category, setCategory] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!category) {
      setError(t("help.errorCategory"));
      return;
    }
    if (description.trim().length < 10) {
      setError(t("help.errorDescription"));
      return;
    }
    setLoading(true);
    try {
      let attachment_path = null;
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        const { data: up } = await api.post("/support/attachment", fd);
        attachment_path = up.path;
      }
      await api.post("/support/tickets", { category, description: description.trim(), attachment_path });
      setDone(true);
    } catch (err) {
      setError(formatApiError(err, t));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-lg mx-auto px-6 py-14" data-testid="help-page">
      <div className="bg-white rounded-2xl border border-slate-100 p-8 shadow-[0_12px_40px_rgba(15,41,66,0.08)]">
        <div className="flex items-center gap-3 mb-2">
          <span className="bg-primary text-white p-2 rounded-xl"><LifeBuoy size={18} /></span>
          <h1 className="text-2xl font-bold text-primary">{t("help.title")}</h1>
        </div>
        {done ? (
          <div className="text-center py-8 space-y-4" data-testid="help-success">
            <CheckCircle2 size={48} className="mx-auto text-emerald-500" />
            <h2 className="text-lg font-semibold text-primary">{t("help.successTitle")}</h2>
            <p className="text-sm text-slate-600">{t("help.successText")}</p>
          </div>
        ) : (
          <>
            <p className="text-sm text-slate-500 mb-6">{t("help.intro")}</p>
            <form onSubmit={submit} className="space-y-5">
              <div>
                <Label>{t("help.category")}</Label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger data-testid="help-category-select" className="mt-1.5 h-12 rounded-xl"><SelectValue placeholder={t("help.categoryPlaceholder")} /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => <SelectItem key={c} value={c} data-testid={`help-category-${c}`}>{t(`help.categories.${c}`)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="help-description">{t("help.description")}</Label>
                <Textarea id="help-description" data-testid="help-description-input" rows={5} value={description} onChange={(e) => setDescription(e.target.value)} placeholder={t("help.descriptionPlaceholder")} className="mt-1.5 rounded-xl" />
              </div>
              <div>
                <Label>{t("help.attachment")}</Label>
                <FilePicker id="help-attachment" testid="help-attachment-input" file={file} onChange={setFile} />
              </div>
              {error && <p className="text-sm text-destructive" data-testid="help-error">{error}</p>}
              <Button data-testid="help-submit-button" type="submit" disabled={loading} className="w-full h-12 rounded-full bg-primary hover:bg-primary/90 text-white font-semibold">{loading ? t("help.sending") : t("help.submit")}</Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
